import RiArrowLeftSLine from '@ben/icons/arrow-left-s-line';
import Button from '@ben/orion/button';
import Drawer from '@ben/orion/drawer';
import IconButton from '@ben/orion/icon-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useMediaQuery } from '@mantine/hooks';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

interface Section {
  id: string;
  title: string;
  children?: Section[];
}

interface SectionNavigationProps {
  sections: Section[];
}

const getAllSectionIds = (sections: Section[]): string[] => {
  const ids: string[] = [];
  for (const section of sections) {
    ids.push(section.id);
    if (section.children) {
      ids.push(...section.children.map((child) => child.id));
    }
  }
  return ids;
};

const useActiveSection = (sections: Section[]) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const sectionIds = getAllSectionIds(sections);
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const intersectingEntries = entries.filter(
        (entry) => entry.isIntersecting
      );
      if (intersectingEntries.length > 0) {
        intersectingEntries.sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
        );
        setActiveSection(intersectingEntries[0].target.id);
      }
    }, observerOptions);

    for (const id of sectionIds) {
      const element = document.querySelector(`#${id}`);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [sections]);

  return activeSection;
};

const NAVIGATION_BREAKPOINT = 1700;

const SectionNavigation = ({ sections }: SectionNavigationProps) => {
  const activeSection = useActiveSection(sections);
  const isSmallScreen = useMediaQuery(
    `(max-width: ${NAVIGATION_BREAKPOINT}px)`
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const theme = useTheme();
  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(`#${sectionId}`);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
      if (isSmallScreen) {
        setIsDrawerOpen(false);
      }
    }
  };

  const handleClick = (
    event: ReactMouseEvent<HTMLButtonElement>,
    sectionId: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    scrollToSection(sectionId);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  if (sections.length === 0) {
    return null;
  }

  const navigationContent = (
    <>
      <Typography variant={TypographyVariant.bodyMd2}>Quick Links</Typography>
      <NavList>
        {sections.map(({ id, title, children }) => (
          <NavItem key={id}>
            <Button
              variant="text"
              onClick={(event) => handleClick(event, id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  scrollToSection(id);
                }
              }}
              bg={
                activeSection === id ? theme.palette.primary.bg : 'transparent'
              }
              color={
                activeSection === id
                  ? theme.palette.primary.main
                  : theme.palette.neutral[100]
              }
              tabIndex={0}
            >
              {title}
            </Button>
            {children && children.length > 0 && (
              <NestedNavList>
                {children.map((child) => (
                  <NavItem key={child.id}>
                    <Button
                      variant="text"
                      onClick={(event) => handleClick(event, child.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          scrollToSection(child.id);
                        }
                      }}
                      bg={
                        activeSection === child.id
                          ? theme.palette.primary.bg
                          : 'transparent'
                      }
                      color={
                        activeSection === child.id
                          ? theme.palette.primary.main
                          : theme.palette.neutral[100]
                      }
                      tabIndex={0}
                    >
                      - {child.title}
                    </Button>
                  </NavItem>
                ))}
              </NestedNavList>
            )}
          </NavItem>
        ))}
      </NavList>
    </>
  );

  if (isSmallScreen) {
    return (
      <>
        <ToggleButtonWrapper $isOpen={isDrawerOpen}>
          <IconButton
            icon={RiArrowLeftSLine}
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            ariaLabel="Toggle Quick Links"
            variant="outline"
            size="md"
          />
        </ToggleButtonWrapper>
        <Drawer
          opened={isDrawerOpen}
          onClose={handleDrawerClose}
          position="right"
          size={280}
        >
          <Drawer.Body>{navigationContent}</Drawer.Body>
        </Drawer>
      </>
    );
  }

  return <NavigationWrapper>{navigationContent}</NavigationWrapper>;
};

export default SectionNavigation;

const NavigationWrapper = styled.nav`
  ${({ theme }) => css`
    position: fixed;
    top: ${theme.spacing(5)};
    right: ${theme.spacing(5)};
    min-width: 200px;
    max-width: 205px;
    padding: ${theme.spacing(3)};
    background: ${theme.palette.neutral[40]};
    border-radius: ${theme.borderRadius.sm};
    border: 1px solid ${theme.palette.neutral[60]};
    z-index: 100;
    max-height: calc(100vh - ${theme.spacing(10)});
    overflow-y: auto;

    @media (max-width: ${NAVIGATION_BREAKPOINT}px) {
      display: none;
    }
  `}
`;

const ToggleButtonWrapper = styled.div<{ $isOpen: boolean }>`
  ${({ $isOpen }) => css`
    position: fixed;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    z-index: 101;
    display: flex;
    align-items: center;
    justify-content: center;

    button {
      svg {
        transform: rotate(${$isOpen ? '180deg' : '0deg'});
        transition: transform 0.2s ease;
      }
    }

    @media (min-width: ${NAVIGATION_BREAKPOINT + 1}px) {
      display: none;
    }
  `}
`;

const NavList = styled.ul`
  ${({ theme }) => css`
    list-style: none;
    padding: 0;
    margin: ${theme.spacing(2, 0, 0, 0)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `}
`;

const NavItem = styled.li`
  margin: 0;
`;

const NestedNavList = styled.ul`
  ${({ theme }) => css`
    list-style: none;
    padding: 0;
    margin: ${theme.spacing(0.5, 0, 0, 2)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.25)};
  `}
`;
